import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PrintActions from '@/shared/components/PrintActions';
import { useQuery } from '@tanstack/react-query';
import { payrollPayments, payrolls, type PayslipItem } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

export default function PayrollPaymentPrintPage() {
  const { id } = useParams();

  const docQ = useQuery({
    queryKey: ['payroll-payment', id ?? ''],
    queryFn: () => payrollPayments.get(id!),
    enabled: !!id,
  });
  const payrollQ = useQuery({
    queryKey: ['payroll', docQ.data?.payroll_id ?? ''],
    queryFn: () => payrolls.get(docQ.data!.payroll_id!),
    enabled: !!docQ.data?.payroll_id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // ч.763: форма з «друк ▾» редактора: ?form=sheet|individual|bank — один документ на форму.
  // Без параметра — старий режим з галками (індивідуально = cash_payout_mode документа).
  const [searchParams] = useSearchParams();
  const fixedForm = searchParams.get('form') as 'sheet' | 'individual' | 'bank' | null;
  const individual = fixedForm ? fixedForm === 'individual' : docQ.data?.cash_payout_mode === 'INDIVIDUAL';
  const slipsQ = useQuery({
    queryKey: ['payroll-payment-payslips', id ?? ''],
    queryFn: () => payrollPayments.payslips(id!),
    enabled: !!id && individual,
  });
  const empQ = useQuery({
    queryKey: ['employees', docQ.data?.organization_id ?? ''],
    queryFn: () => refs.employees(docQ.data!.organization_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const empName = useMemo(() => {
    const m = new Map<string, { name: string; code: string | null; position?: string | null }>();
    empQ.data?.forEach((e) => m.set(e.id, { name: e.name, code: e.code, position: e.position_name }));
    return m;
  }, [empQ.data]);
  // ч.737 (зауваження власника): П-53 — КАСОВИЙ документ (підписи про
  // отримання готівки), як у 1С відомість «через касу». Банківські рядки у
  // П-53 не потрапляють — для них окрема «Відомість на перерахування на
  // карткові рахунки» (додаток до зарплатного реєстру в банк).
  const [showCashState, setShowCash] = useState(true);
  const [showBankState, setShowBank] = useState(true);
  const showCash = fixedForm ? fixedForm !== 'bank' : showCashState;
  const showBank = fixedForm ? fixedForm === 'bank' : showBankState;

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);
  const periodLabel = payrollQ.data
    ? `${MONTHS_UK[payrollQ.data.period_month]} ${payrollQ.data.period_year}`
    : null;

  const methodOf = (it: { payment_method?: string | null }) =>
    (it.payment_method || doc.payment_method || 'CASH') === 'BANK' ? 'BANK' : 'CASH';
  const cashRows = doc.items.filter((it) => methodOf(it) === 'CASH');
  const bankRows = doc.items.filter((it) => methodOf(it) === 'BANK');
  const cashTotal = cashRows.reduce((s, i) => s + Number(i.amount || 0), 0);
  const bankTotal = bankRows.reduce((s, i) => s + Number(i.amount || 0), 0);
  const cell = (extra: React.CSSProperties = {}): React.CSSProperties =>
    ({ border: '1px solid #000', padding: '0.3rem', ...extra });
  const docTitle = `№ ${doc.document_number} від ${new Date(doc.document_date).toLocaleDateString('uk-UA')}`;
  const renderCash = showCash && cashRows.length > 0;
  const renderBank = showBank && bankRows.length > 0;

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt', lineHeight: 1.3,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div className="print-hide" style={{
        position: 'sticky', top: 0, background: '#f1f5f9',
        padding: '0.5rem 1rem', marginBottom: '1rem', borderRadius: 6,
        display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap',
      }}>
        <strong>
          {fixedForm === 'sheet' ? 'Платіжна відомість П-53' : fixedForm === 'individual'
            ? 'Індивідуальні відомості' : fixedForm === 'bank' ? 'Відомість на карткові рахунки' : 'Друк відомості виплати'}
        </strong>
        {!fixedForm && <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          title={individual
            ? 'Індивідуальні відомості: окремий аркуш кожному з розрахунковим листком (колеги не бачать чужих сум)'
            : 'Платіжна відомість (типова форма П-53) — лише рядки «Готівка»: підписи про отримання'}>
          <input type="checkbox" checked={showCash} disabled={cashRows.length === 0}
            onChange={(e) => setShowCash(e.target.checked)} style={{ width: 'auto' }} />
          {individual ? 'індивідуальні відомості, готівка' : 'платіжна відомість, готівка'} ({cashRows.length})
        </label>}
        {!fixedForm && <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          title="Відомість на перерахування на карткові рахунки — рядки «Банк» (додаток до реєстру в банк)">
          <input type="checkbox" checked={showBank} disabled={bankRows.length === 0}
            onChange={(e) => setShowBank(e.target.checked)} style={{ width: 'auto' }} />
          відомість на перерахування, банк ({bankRows.length})
        </label>}
      </div>

      <PrintActions docMeta={{
        organizationId: doc.organization_id,
        documentType: 'doc.payroll_payments',
        documentId: doc.id,
        filename: `${fixedForm === 'bank' ? 'Відомість на карткові рахунки' : individual ? 'Індивідуальні відомості' : 'Платіжна відомість П-53'} ${doc.document_number}`,
        orgName: org?.name ?? null,
      }} />

      {!renderCash && !renderBank && (
        <p style={{ color: '#64748b' }}>Немає рядків для друку.</p>
      )}

      {renderCash && individual && (
        <IndividualSheets
          rows={cashRows}
          slips={slipsQ.data?.items ?? []}
          empName={empName}
          orgName={org?.full_name ?? org?.name ?? ''}
          orgCodes={partyCodesText(org)}
          docTitle={docTitle}
          periodLabel={periodLabel}
          linkedPayroll={slipsQ.data?.linked_payroll ?? false}
        />
      )}

      {renderCash && !individual && (
        <section>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
            {partyCodesText(org) && (
              <div style={{ fontSize: '9pt', color: '#555' }}>{partyCodesText(org)}</div>
            )}
          </div>
          <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.5rem 0' }}>
            ПЛАТІЖНА ВІДОМІСТЬ
          </h2>
          <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>{docTitle}</p>
          {periodLabel && (
            <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
              на виплату заробітної плати за {periodLabel} р.
            </p>
          )}
          <p style={{ textAlign: 'center', margin: '0.3rem 0', fontSize: '9pt', color: '#555' }}>
            Спосіб виплати: готівкою з каси
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '1rem',
            border: '1px solid #000' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={cell({ width: '5%' })}>№</th>
                <th style={cell({ width: '10%' })}>Таб. №</th>
                <th style={cell({ textAlign: 'left' })}>Прізвище, ім'я, по батькові</th>
                <th style={cell({ width: '18%' })}>Сума до виплати, грн</th>
                <th style={cell({ width: '25%' })}>Підпис про отримання</th>
              </tr>
            </thead>
            <tbody>
              {cashRows.map((it, idx) => {
                const emp = empName.get(it.employee_id);
                return (
                  <tr key={it.id ?? it.line_number}>
                    <td style={cell({ textAlign: 'center' })}>{idx + 1}</td>
                    <td style={cell({ textAlign: 'center' })}>{emp?.code ?? '—'}</td>
                    <td style={cell()}>{emp?.name ?? '—'}</td>
                    <td style={cell({ textAlign: 'right' })}>{fmt(it.amount)}</td>
                    <td style={cell({ minHeight: 24 })}></td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                <td colSpan={3} style={cell({ textAlign: 'right' })}>Усього до виплати:</td>
                <td style={cell({ textAlign: 'right' })}>{fmt(cashTotal)}</td>
                <td style={{ border: '1px solid #000' }}></td>
              </tr>
            </tbody>
          </table>

          <p style={{ marginTop: '1rem' }}>
            За цією відомістю видано суму у розмірі&nbsp;
            <strong>{fmt(cashTotal)}</strong> грн ({moneyToUkrWords(cashTotal)}).
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            Кількість одержувачів: <strong>{cashRows.length}</strong>.
          </p>

          <table style={{ width: '100%', marginTop: '3rem' }}>
            <tbody>
              <tr>
                <td style={{ width: '50%', verticalAlign: 'top' }}>
                  <div>Видав (касир):</div>
                  <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                    (посада, підпис, П.І.Б.)
                  </div>
                </td>
                <td style={{ width: '50%', verticalAlign: 'top' }}>
                  <div>Перевірив (бухгалтер):</div>
                  <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                    (підпис, П.І.Б.)
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {renderBank && (
        <section style={renderCash ? { pageBreakBefore: 'always', paddingTop: '10mm' } : undefined}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
            {partyCodesText(org) && (
              <div style={{ fontSize: '9pt', color: '#555' }}>{partyCodesText(org)}</div>
            )}
          </div>
          <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.5rem 0' }}>
            ВІДОМІСТЬ НА ПЕРЕРАХУВАННЯ ЗАРОБІТНОЇ ПЛАТИ НА КАРТКОВІ РАХУНКИ
          </h2>
          <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>{docTitle}</p>
          {periodLabel && (
            <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
              за {periodLabel} р.
            </p>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '1rem',
            border: '1px solid #000' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={cell({ width: '5%' })}>№</th>
                <th style={cell({ width: '10%' })}>Таб. №</th>
                <th style={cell({ textAlign: 'left' })}>Прізвище, ім'я, по батькові</th>
                <th style={cell({ width: '30%' })}>IBAN одержувача</th>
                <th style={cell({ width: '16%' })}>Сума, грн</th>
              </tr>
            </thead>
            <tbody>
              {bankRows.map((it, idx) => {
                const emp = empName.get(it.employee_id);
                return (
                  <tr key={it.id ?? it.line_number}>
                    <td style={cell({ textAlign: 'center' })}>{idx + 1}</td>
                    <td style={cell({ textAlign: 'center' })}>{emp?.code ?? '—'}</td>
                    <td style={cell()}>{emp?.name ?? '—'}</td>
                    <td style={cell({ fontFamily: 'monospace', fontSize: '8.5pt' })}>{it.recipient_iban ?? '—'}</td>
                    <td style={cell({ textAlign: 'right' })}>{fmt(it.amount)}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                <td colSpan={4} style={cell({ textAlign: 'right' })}>Усього до перерахування:</td>
                <td style={cell({ textAlign: 'right' })}>{fmt(bankTotal)}</td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginTop: '1rem' }}>
            Усього до перерахування&nbsp;<strong>{fmt(bankTotal)}</strong> грн
            ({moneyToUkrWords(bankTotal)}). Кількість одержувачів: <strong>{bankRows.length}</strong>.
          </p>
          <table style={{ width: '100%', marginTop: '3rem' }}>
            <tbody>
              <tr>
                <td style={{ width: '50%', verticalAlign: 'top' }}>
                  <div>Керівник:</div>
                  <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
                </td>
                <td style={{ width: '50%', verticalAlign: 'top' }}>
                  <div>Головний бухгалтер:</div>
                  <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ч.738: індивідуальна відомість — один аркуш на працівника: сума до видачі,
// підпис про отримання + розрахунковий листок з повʼязаного нарахування
// (як «Розрахунковий листок» 1С; без нарахування — лише сума і борг).
function IndividualSheets({ rows, slips, empName, orgName, orgCodes, docTitle, periodLabel, linkedPayroll }: {
  rows: Array<{ id?: string; line_number: number; employee_id: string; amount: string }>;
  slips: PayslipItem[];
  empName: Map<string, { name: string; code: string | null; position?: string | null }>;
  orgName: string; orgCodes: string; docTitle: string; periodLabel: string | null;
  linkedPayroll: boolean;
}) {
  const slipBy = new Map(slips.map((sl) => [sl.employee_id, sl]));
  const cell = (extra: React.CSSProperties = {}): React.CSSProperties =>
    ({ border: '1px solid #000', padding: '0.25rem 0.4rem', ...extra });
  const num = (v: string | number | null | undefined) => v == null ? '—' : fmt(v);
  return (
    <>
      {rows.map((it, idx) => {
        const emp = empName.get(it.employee_id);
        const sl = slipBy.get(it.employee_id);
        const acc = sl?.accrued ?? null;
        const amount = Number(it.amount || 0);
        const mismatch = !!acc && Math.abs(Number(acc.net_amount) - amount) > 0.005;
        return (
          <section key={it.id ?? it.line_number}
            style={idx > 0 ? { pageBreakBefore: 'always', paddingTop: '10mm' } : undefined}>
            <div style={{ textAlign: 'center', marginBottom: '0.6rem' }}>
              <div style={{ fontSize: '10pt' }}>{orgName}</div>
              {orgCodes && <div style={{ fontSize: '9pt', color: '#555' }}>{orgCodes}</div>}
            </div>
            <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.4rem 0' }}>
              ПЛАТІЖНА ВІДОМІСТЬ (індивідуальна)
            </h2>
            <p style={{ textAlign: 'center', margin: '0.2rem 0' }}>{docTitle}, аркуш {idx + 1} з {rows.length}</p>
            {periodLabel && (
              <p style={{ textAlign: 'center', margin: '0.2rem 0' }}>
                на виплату заробітної плати за {periodLabel} р.
              </p>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt', marginTop: '0.8rem' }}>
              <tbody>
                <tr>
                  <td style={cell({ width: '22%', background: '#f8fafc' })}>Працівник</td>
                  <td style={cell()}>{emp?.name ?? '—'}{emp?.position ? `, ${emp.position}` : ''}</td>
                  <td style={cell({ width: '12%', background: '#f8fafc' })}>Таб. №</td>
                  <td style={cell({ width: '14%', textAlign: 'center' })}>{emp?.code ?? '—'}</td>
                </tr>
                <tr>
                  <td style={cell({ background: '#f8fafc' })}>До видачі, грн</td>
                  <td style={cell({ fontWeight: 700 })} colSpan={3}>
                    {fmt(amount)} ({moneyToUkrWords(amount)})
                  </td>
                </tr>
              </tbody>
            </table>

            {acc ? (
              <>
                <h3 style={{ fontSize: '10.5pt', margin: '0.9rem 0 0.3rem' }}>Розрахунковий листок</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={cell({ textAlign: 'left', width: '50%' })}>Нараховано</th>
                      <th style={cell({ width: '15%' })}>Сума</th>
                      <th style={cell({ textAlign: 'left' })}>Утримано / довідково</th>
                      <th style={cell({ width: '15%' })}>Сума</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const left: Array<[string, string]> = (sl?.accrual_lines?.length
                        ? sl.accrual_lines.map((l) => [l.name, num(l.amount)] as [string, string])
                        : ([
                          ['Оклад / тариф', num(acc.salary_amount)],
                          ['Премія', num(acc.bonus_amount)],
                        ] as Array<[string, string]>).filter(([, v]) => v !== '—'));
                      left.push(['Разом нараховано', num(acc.gross_amount)]);
                      const right: Array<[string, string]> = [
                        ['ПДФО', num(acc.pdfo_amount)],
                        ['Військовий збір', num(acc.wz_amount)],
                        ['Разом утримано', fmt(Number(acc.pdfo_amount) + Number(acc.wz_amount))],
                        ['Аванс (виплачено раніше)', num(acc.advance_amount)],
                        ['Відпрацьовано днів / норма', `${acc.working_days ?? '—'} / ${acc.norm_days ?? '—'}`],
                        ['Відпрацьовано годин / норма', `${acc.worked_hours ?? '—'} / ${acc.norm_hours ?? '—'}`],
                      ];
                      const n = Math.max(left.length, right.length);
                      return Array.from({ length: n }, (_, i) => (
                        <tr key={i}>
                          <td style={cell()}>{left[i]?.[0] ?? ''}</td>
                          <td style={cell({ textAlign: 'right' })}>{left[i]?.[1] ?? ''}</td>
                          <td style={cell()}>{right[i]?.[0] ?? ''}</td>
                          <td style={cell({ textAlign: 'right' })}>{right[i]?.[1] ?? ''}</td>
                        </tr>
                      ));
                    })()}
                    <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                      <td style={cell()} colSpan={3}>До виплати за нарахуванням</td>
                      <td style={cell({ textAlign: 'right' })}>{num(acc.net_amount)}</td>
                    </tr>
                  </tbody>
                </table>
                {mismatch && (
                  <p style={{ fontSize: '8.5pt', color: '#555', margin: '0.3rem 0' }}>
                    За цією відомістю видається {fmt(amount)} грн (часткова виплата або кілька відомостей).
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: '8.5pt', color: '#555', margin: '0.6rem 0' }}>
                {linkedPayroll
                  ? 'У повʼязаному нарахуванні немає рядка цього працівника — розрахунковий листок не друкується.'
                  : 'Відомість не привʼязана до нарахування — друкується лише сума до видачі.'}
              </p>
            )}

            {sl && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '0.6rem' }}>
                <tbody>
                  <tr>
                    <td style={cell({ background: '#f8fafc' })}>Борг на початок періоду</td>
                    <td style={cell({ textAlign: 'right', width: '18%' })}>{num(sl.debt_start)}</td>
                    <td style={cell({ background: '#f8fafc' })}>Нараховано за період</td>
                    <td style={cell({ textAlign: 'right', width: '18%' })}>{num(sl.accrued_period)}</td>
                  </tr>
                  <tr>
                    <td style={cell({ background: '#f8fafc' })}>Виплачено раніше за період</td>
                    <td style={cell({ textAlign: 'right' })}>{num(sl.paid_before)}</td>
                    <td style={cell({ background: '#f8fafc' })}>Борг на кінець (після цієї видачі)</td>
                    <td style={cell({ textAlign: 'right' })}>{num(sl.debt_end)}</td>
                  </tr>
                </tbody>
              </table>
            )}

            <table style={{ width: '100%', marginTop: '2rem' }}>
              <tbody>
                <tr>
                  <td style={{ width: '50%', verticalAlign: 'top' }}>
                    <div>Отримав:</div>
                    <div style={{ marginTop: '1.6rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                      ___________________
                    </div>
                    <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б., дата)</div>
                  </td>
                  <td style={{ width: '50%', verticalAlign: 'top' }}>
                    <div>Видав (касир):</div>
                    <div style={{ marginTop: '1.6rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                      ___________________
                    </div>
                    <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(посада, підпис, П.І.Б.)</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        );
      })}
    </>
  );
}

// Сума прописом — спрощена реалізація (тільки гривні + копійки).
function moneyToUkrWords(value: number): string {
  const hrn = Math.floor(value);
  const kop = Math.round((value - hrn) * 100);
  return `${numberToUkrWords(hrn)} грн ${String(kop).padStart(2, '0')} коп.`;
}

const ONES = ['', 'один', 'два', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"];
const ONES_F = ['', 'одна', 'дві', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"];
const TEENS = ['десять', 'одинадцять', 'дванадцять', 'тринадцять', 'чотирнадцять',
  "п'ятнадцять", 'шістнадцять', 'сімнадцять', 'вісімнадцять', "дев'ятнадцять"];
const TENS = ['', '', 'двадцять', 'тридцять', 'сорок', "п'ятдесят",
  'шістдесят', 'сімдесят', 'вісімдесят', "дев'яносто"];
const HUNDREDS = ['', 'сто', 'двісті', 'триста', 'чотириста', "п'ятсот",
  'шістсот', 'сімсот', 'вісімсот', "дев'ятсот"];

function triadToWords(n: number, feminine: boolean): string {
  if (n === 0) return '';
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;
  const parts: string[] = [];
  if (h) parts.push(HUNDREDS[h]!);
  if (t === 1) {
    parts.push(TEENS[o]!);
  } else {
    if (t) parts.push(TENS[t]!);
    if (o) parts.push((feminine ? ONES_F[o] : ONES[o])!);
  }
  return parts.join(' ');
}

function thousandsForm(n: number): string {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'тисяч';
  if (last === 1) return 'тисяча';
  if (last >= 2 && last <= 4) return 'тисячі';
  return 'тисяч';
}

function millionsForm(n: number): string {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'мільйонів';
  if (last === 1) return 'мільйон';
  if (last >= 2 && last <= 4) return 'мільйони';
  return 'мільйонів';
}

function numberToUkrWords(n: number): string {
  if (n === 0) return 'нуль';
  if (n < 0) return 'мінус ' + numberToUkrWords(-n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(`${triadToWords(millions, false)} ${millionsForm(millions)}`);
  if (thousands) parts.push(`${triadToWords(thousands, true)} ${thousandsForm(thousands)}`);
  if (rest) parts.push(triadToWords(rest, false));
  return parts.join(' ').trim();
}
